import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppDispatch, AppThunk } from "@/app/types";
import {
  CreateProductOrderRequest,
  CreateProductOrderApiResponse,
  ProductOrderResponse,
} from "@/modules/productStorefront/features/ProductCheckout/api/createProductOrder/types";
import { createProductOrder } from "@/modules/productStorefront/features/ProductCheckout/api/createProductOrder/createProductOrderApi";

export interface CartItem {
  productId: number;
  name: string;
  price: number;
  quantity: number;
  productType: string;
  uuid: string;
}

interface ProductCheckoutState {
  cartItems: CartItem[];
  loading: boolean;
  error: string | null;
  orderResult: ProductOrderResponse | null;
}

const initialState: ProductCheckoutState = {
  cartItems: [],
  loading: false,
  error: null,
  orderResult: null,
};

const productCheckoutSlice = createSlice({
  name: "productCheckout",
  initialState,
  reducers: {
    addToCart(state, action: PayloadAction<CartItem>) {
      const existingItem = state.cartItems.find(
        (item) => item.productId === action.payload.productId,
      );
      if (existingItem) {
        existingItem.quantity += action.payload.quantity;
      } else {
        state.cartItems.push(action.payload);
      }
    },
    removeFromCart(state, action: PayloadAction<number>) {
      state.cartItems = state.cartItems.filter(
        (item) => item.productId !== action.payload,
      );
    },
    updateCartItemQuantity(
      state,
      action: PayloadAction<{ productId: number; quantity: number }>,
    ) {
      const item = state.cartItems.find(
        (item) => item.productId === action.payload.productId,
      );
      if (item) {
        item.quantity = action.payload.quantity;
      }
    },
    clearCart(state) {
      state.cartItems = [];
    },
    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
    },
    setOrderResult(state, action: PayloadAction<ProductOrderResponse | null>) {
      state.orderResult = action.payload;
    },
    resetCheckoutState() {
      return initialState;
    },
  },
});

export default productCheckoutSlice.reducer;

export const {
  addToCart,
  removeFromCart,
  updateCartItemQuantity,
  clearCart,
  setLoading,
  setError,
  setOrderResult,
  resetCheckoutState,
} = productCheckoutSlice.actions;

export const createProductOrderAction =
  (
    requestData: CreateProductOrderRequest,
    onSuccess?: (data: ProductOrderResponse) => void,
  ): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(setLoading(true));
    dispatch(setError(null));
    try {
      const response: CreateProductOrderApiResponse =
        await createProductOrder(requestData);
      dispatch(setOrderResult(response.data));
      dispatch(clearCart());
      if (onSuccess) {
        onSuccess(response.data);
      }
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to create product order.";
      dispatch(setError(message));
    } finally {
      dispatch(setLoading(false));
    }
  };
